import { seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, card, cardArt, cardName, fade, fill, place, sprite,
} from './common';

/**
 * **素材を使う召喚の変身バンクの共通の部品**（Issue #598 / #599 / #648。シンクロ・融合・アドバンス召喚）。
 * 疑似イベント `material_summon` の 3 本（`../effects/material_summon_*_effect.js`）が同じ骨組みを使う:
 *
 * - `bankFrame` —— 暗幕・はけ（縦に細く潰れて消え、最後に横の硬い線 1 フレーム）・描く順の group・銀の金属文字
 * - `sideRow` —— 素材のカードの横の並び（画面の中央を空け、左と右に分ける）
 * - `materialCards` —— 素材のカード（召喚した人の側から飛んできて並ぶ。数式の項を叩くと揺れて硬い縁が 2 フレーム白く光る。変身の閃光）
 * - `formulaRow` —— 数式（左から叩き込む → 中央の 1 点へ吸われて消える）
 * - `monsterPlate` —— 出るモンスター（鋼の板とイラストが縦の細い隙間から開き、影 → 色。名前・帯。セットなら裏のカードで名前なし）
 *
 * 見た目の値（大きさ・位置・描く順）は v3 のシンクロのまま（ユーザー 2026-10-01 のレビューで決まったもの）。
 */
export const EXIT_LEN = 180;
export const PLATE = Object.freeze({
  W: 540, H: 700, BAND: 110, ART_W: 450, CY: 570, BAND_Y: 985,
});

/**
 * **暗幕とはけ**。暗幕は潰さない（はけで戻す）。それ以外は `bank` に入れて、はけで縦に潰す。
 * three の透明物は一番近い祖先の group の renderOrder で並ぶので、描く順ごとに `layer(n)` で group を分ける。
 */
export function bankFrame(ctx, { dur, dim: dimColor = '#050507', dimAlpha = 0.55, fly = [0, 300] }) {
  const { THREE, kit } = ctx;
  const W = ctx.stage?.canvas?.w ?? 1920;
  const H = ctx.stage?.canvas?.h ?? 1080;
  const MID = H / 2;
  const EXIT = dur - EXIT_LEN - 20;
  const EXIT_END = EXIT + EXIT_LEN;
  const top = new THREE.Group();
  const dim = add(top, place(fill(ctx, dimColor, W, H), W / 2, H / 2), 0);
  const squash = add(top, new THREE.Group(), 1);
  const bank = add(squash, new THREE.Group(), 1);
  squash.position.set(0, MID, 0);
  bank.position.set(0, -MID, 0);
  const groups = {};
  const layer = (n) => { if (!groups[n]) groups[n] = add(bank, new THREE.Group(), n); return groups[n]; };
  const metal = (text, size, italic = false) => {
    const tx = kit.metalTextTexture(text, { size, edge: null, italic });
    const m = sprite(ctx, tx.texture, tx.width, tx.height);
    const pad = Math.max(60, size * 0.14 + size * 0.11 * 2 + 10);
    return { m, w: tx.width, h: tx.height, glyph: Math.max(10, tx.width - pad * 2) };
  };
  const exitLine = add(top, fill(ctx, '#ffffff', 1, 1), 15);
  const update = (t) => {
    fade(dim, dimAlpha * tween(t, fly[0], fly[1], 'ease_out') * (1 - seg(t, EXIT, EXIT_END)));
    const ex = tween(t, EXIT, EXIT_END, 'power2_in');
    squash.scale.set(1 + 0.12 * ex, Math.max(0.001, 1 - ex), 1);
    squash.visible = t < EXIT_END;
    place(exitLine, W / 2, MID, { sx: W * 0.8, sy: 4 });
    fade(exitLine, t >= EXIT_END - 34 && t < EXIT_END ? 0.9 : 0);
  };
  return {
    top, bank, layer, metal, update, W, H, EXIT, EXIT_END,
  };
}

/**
 * **素材のカードの横の並び**（純関数）: 画面の中央を半径 `hole` だけ空け、`nL` 枚は中央の左の外側から左へ、
 * `nR` 枚は右の外側から右へ並べる。はみ出すなら側ごとに間を詰める。返すのは各カードの x（左 → 右の側の順）と中央の x。
 */
export function sideRow(nL, nR, cw, { W = 1920, hole = 250 } = {}) {
  const cx = W / 2;
  const gap = cw + 60;
  const inner = hole + 40 + cw / 2;           // 中央から一番内側のカードの中心まで
  const room = W / 2 - 40 - cw / 2 - inner;    // 一番内側から一番外側のカードの中心まで使える幅
  const side = (k) => (k > 1 ? Math.min(gap, Math.max(0, room) / (k - 1)) : 0);
  const gL = side(nL); const gR = side(nR);
  const left = Array.from({ length: nL }, (_, i) => cx - inner - (nL - 1 - i) * gL);
  const right = Array.from({ length: nR }, (_, i) => cx + inner + i * gR);
  return { xs: [...left, ...right], tx: cx };
}

/**
 * **素材のカード**: 召喚した人の側から飛んできて `xs` に並ぶ。`hitsOf(i)` の時刻ごとに揺れて硬い縁が 2 フレーム白く光る。
 * `pose(mc, t)` が飛ぶ・揺れる間の位置、`edges(mc, x, y, s, lit)` が硬い縁、`mc.flash` はカードの形の閃光（使う側が出す）。
 */
export function materialCards(ctx, frame, {
  materials, player, xs, y, cw, hitsOf = () => [], rand, fly = [0, 300], order = { card: 4, edge: 7, flash: 9 },
}) {
  const W = frame.W; const H = frame.H;
  const cx = W / 2;
  const ch = cw / CARD_ASPECT;
  const p = player === 1 ? 1 : 0;
  const fromY = p === 1 ? -ch : H + ch;
  const items = materials.map((m, i) => {
    const c = card(ctx, { code: m.code, player: p, faceUp: true, w: cw });
    add(frame.layer(order.card), c.mesh, order.card);
    const edge = [0, 1, 2, 3].map(() => add(frame.layer(order.edge), fill(ctx, '#ffffff', 1, 1), order.edge));
    const flash = add(frame.layer(order.flash), fill(ctx, '#ffffff', cw, ch), order.flash);
    const x = xs[i];
    return {
      m, c, i, x, y, hits: hitsOf(i) ?? [], edge, flash, delay: i * 40, fromX: cx + (x - cx) * 0.4 + (rand() - 0.5) * 200,
    };
  });
  const pose = (mc, t) => {
    const k = tween(t, fly[0] + mc.delay, fly[1], 'power3_out');
    const hit = mc.hits.find((h) => t >= h && t < h + 180);
    const quiver = hit != null ? Math.sin((t - hit) / 9) * 7 * (1 - seg(t, hit, hit + 180)) : 0;
    const bump = hit != null ? 1 + 0.05 * Math.sin(seg(t, hit, hit + 140) * Math.PI) : 1;
    return {
      x: mc.fromX + (mc.x - mc.fromX) * k + quiver,
      y: fromY + (mc.y - fromY) * k,
      s: bump,
      rot: (1 - k) * (p === 1 ? -0.4 : 0.4),
      lit: mc.hits.some((h) => t >= h && t < h + 34),
    };
  };
  const edges = (mc, x, y, s, lit, rot = 0) => {
    const hw = (cw * s) / 2 + 6; const hh = (ch * s) / 2 + 6; const th = 6;
    const c = Math.cos(rot); const sn = Math.sin(rot);
    [[0, -hh, 2 * hw + th, th], [hw, 0, th, 2 * hh + th], [0, hh, 2 * hw + th, th], [-hw, 0, th, 2 * hh + th]].forEach(([ox, oy, bw, bh], j) => {
      place(mc.edge[j], x + ox * c - oy * sn, y + ox * sn + oy * c, { sx: bw, sy: bh, rot });
      fade(mc.edge[j], lit ? 1 : 0);
    });
  };
  return {
    items, pose, edges, cw, ch,
  };
}

/**
 * **数式**（`tokens` = `{ text, size, at, gold?, color? }` の並び）。左から並べて組み、`update(t, { y, toX, suck, hideAt })` で
 * 叩き込む（`slamFrom` 倍（既定 2.2）→ 1 倍）→ `suck`（0→1）で `toX` へ吸われて消える。`hideAt` 以降は出さない。`maxW` を超える幅なら全体を縮める
 */
export function formulaRow(ctx, frame, tokens, {
  gap = 22, order = 10, maxW = Infinity, slamFrom = 2.2,
} = {}) {
  const cx = frame.W / 2;
  const built = tokens.map((tk) => ({ ...tk, ...frame.metal(tk.text, tk.size, true) }));
  const total = built.reduce((a, b) => a + b.glyph, 0) + gap * Math.max(0, built.length - 1);
  const fit = Math.min(1, maxW / Math.max(1, total));
  let x = cx - (total * fit) / 2;
  built.forEach((b) => {
    b.x = x + (b.glyph * fit) / 2; x += (b.glyph + gap) * fit;
    if (b.gold) b.m.material.color?.set?.('#ffcf5a');
    else if (b.color) b.m.material.color?.set?.(b.color);
    add(frame.layer(order), b.m, order);
  });
  const update = (t, {
    y, toX = cx, suck = 0, hideAt = Infinity,
  }) => {
    built.forEach((b) => {
      const slam = tween(t, b.at - 50, b.at, 'power2_in');
      const settle = t >= b.at ? 1 + 0.07 * Math.sin(seg(t, b.at, b.at + 140) * Math.PI) : 1;
      const s = (slamFrom - (slamFrom - 1) * slam) * settle * (1 - 0.7 * suck) * fit;
      place(b.m, b.x + (toX - b.x) * suck, y, { sx: s, sy: s * (1 - 0.6 * suck) });
      fade(b.m, t >= b.at - 50 && t < hideAt ? seg(t, b.at - 50, b.at - 30) : 0);
    });
  };
  return { tokens: built, update, fit };
}

/**
 * **出るモンスター**（鋼の板・イラスト・影・名前・召喚の帯）。画面の中央に固定、イラストは正立（`cardArt`）。
 * `result.faceDown`（セット）なら**絵も名前も見せない**: イラストの代わりにその人のスリーブの裏のカード、名前なし。
 * `update(t, { at, colorAt: [a, b], nameAt, bandAt })`。
 */
export function monsterPlate(ctx, frame, {
  result, player, pc, accent, bandColor, tag, cx = frame.W / 2,
}) {
  const { THREE, kit } = ctx;
  const W = frame.W;
  const { layer } = frame;
  const ART_CY = PLATE.CY - PLATE.H / 2 + 50 + PLATE.ART_W / 2;
  const NAME_Y = PLATE.CY + PLATE.H / 2 - PLATE.BAND / 2 - 2;
  const faceDown = Boolean(result.faceDown);
  const plate = add(layer(12), sprite(ctx, kit.steelPlateTexture(PLATE.W, PLATE.H, { cut: 56, band: PLATE.BAND, accent }), PLATE.W, PLATE.H), 12);
  let art; let artY; let artH;
  if (faceDown) {
    // 裏のカード（板の絵と名前の段をまたいで中央）
    const back = card(ctx, { code: null, player, faceUp: false, w: 360 });
    art = { mesh: back.mesh };
    artH = back.h;
    artY = (PLATE.CY - PLATE.H / 2 + 50 + PLATE.CY + PLATE.H / 2 - 50) / 2;
  } else {
    art = cardArt(ctx, { code: result.code, player, w: PLATE.ART_W });
    artH = art.h;
    artY = ART_CY;
  }
  add(layer(12.5), art.mesh, 12.5);
  const shadow = add(layer(12.6), fill(ctx, '#000000', faceDown ? 360 : PLATE.ART_W, artH), 12.6);
  const name = faceDown ? '' : cardName(ctx, result.code);
  const word = name ? frame.metal(name, 72) : null;
  const fit = word ? Math.min(1, (PLATE.W - 180) / Math.max(1, word.glyph)) : 1;   // 下の段の鋲を避ける
  if (word) add(layer(13), word.m, 13);
  const band = add(layer(13), new THREE.Group(), 13);
  const BW = W + 200; const BH = 112;
  const put = (m, y, ord) => { m.position.set(0, y, 0); add(band, m, ord); };
  put(fill(ctx, '#0c0d11', BW, BH), 0, 13);
  put(fill(ctx, bandColor, BW, 4), -BH / 2 + 10, 13.1);
  put(fill(ctx, bandColor, BW, 4), BH / 2 - 10, 13.1);
  put(fill(ctx, pc, BW, 7), -BH / 2 + 1, 13.1);
  put(fill(ctx, pc, BW, 7), BH / 2 - 1, 13.1);
  const tagM = frame.metal(tag, 84, true);
  add(layer(13.5), tagM.m, 13.5);

  const update = (t, {
    at, open: openMs = 120, colorAt, nameAt, bandAt,
  }) => {
    const open = tween(t, at, at + openMs, 'expo_out');
    const sx = Math.max(0.001, 0.03 + 0.97 * open);
    const shown = t >= at;
    place(plate, cx, PLATE.CY, { sx, sy: 1 });
    fade(plate, shown ? 1 : 0);
    place(art.mesh, cx, artY, { sx, sy: 1 });
    fade(art.mesh, shown ? 1 : 0);
    place(shadow, cx, artY, { sx, sy: 1 });
    fade(shadow, shown ? 1 - seg(t, colorAt[0], colorAt[1]) : 0);
    if (word) {
      const ns = tween(t, nameAt - 50, nameAt, 'power2_in');
      const bounce = t >= nameAt ? 1 - 0.06 * Math.sin(seg(t, nameAt, nameAt + 140) * Math.PI) : 1;
      const s = (1.5 - 0.5 * ns) * bounce * fit;
      place(word.m, cx, NAME_Y + 90 * (1 - ns), { s });
      fade(word.m, t >= nameAt - 50 ? seg(t, nameAt - 50, nameAt - 30) : 0);
    }
    const bOpen = tween(t, bandAt - 20, bandAt + 40, 'expo_out');
    place(band, W / 2, PLATE.BAND_Y, { sx: 1, sy: Math.max(0.001, bOpen) });
    fade(band, t >= bandAt - 20 ? 1 : 0);
    const ts = tween(t, bandAt - 30, bandAt, 'power2_in');
    const tb = t >= bandAt ? 1 + 0.05 * Math.sin(seg(t, bandAt, bandAt + 140) * Math.PI) : 1;
    place(tagM.m, W / 2, PLATE.BAND_Y, { s: (2.4 - 1.4 * ts) * tb });
    fade(tagM.m, t >= bandAt - 30 ? seg(t, bandAt - 30, bandAt - 10) : 0);
  };
  return { update, hasName: Boolean(word), faceDown };
}
